<script setup lang="ts">
import { Mail, Plus, Trash2, UserRound } from 'lucide-vue-next';
import ConfirmDialog from '@/components/editing/ConfirmDialog.vue';
import DetailsSheet from '@/components/editing/DetailsSheet.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'vue-sonner';
import { CLIENT_LIFECYCLE_STATUS_LABELS } from '~/lib/clientLifecycle';
import type { Client, ClientCommercialStage, ClientContact, ClientDeliveryStage, ClientLifecycleStatus, ClientPipelineStage, ClientSourceType } from '~/types/models';

const props = defineProps<{
    open: boolean;
    client: Client | null;
    save: (patch: Partial<Client>) => void | Promise<void>;
    canDelete?: boolean;
    deleting?: boolean;
}>();

const emit = defineEmits<{
    (event: 'update:open', value: boolean): void;
    (event: 'add-contact'): void;
    (event: 'edit-contact', contact: ClientContact): void;
    (event: 'delete'): void;
}>();

const saving = ref(false);
const deleteConfirmationOpen = ref(false);
const lifecycleOptions = Object.entries(CLIENT_LIFECYCLE_STATUS_LABELS).map(([value, label]) => ({
    value: value as ClientLifecycleStatus,
    label,
}));
const entityTypeOptions = [
    { value: 'organization', label: 'Organization' },
    { value: 'individual', label: 'Individual' },
] as const;
const sourceOptions: Array<{ value: ClientSourceType; label: string }> = [
    { value: 'manual', label: 'Manual' },
    { value: 'form_submission', label: 'Form submission' },
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
];
const commercialStageOptions: Array<{ value: ClientCommercialStage; label: string }> = [
    { value: 'lead', label: 'Lead' },
    { value: 'opportunity', label: 'Opportunity' },
    { value: 'client', label: 'Client' },
    { value: 'past_client', label: 'Past client' },
    { value: 'advocate', label: 'Advocate' },
];
const pipelineStageOptions: Array<{ value: ClientPipelineStage; label: string }> = [
    { value: 'new_enquiry', label: 'New enquiry' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal_sent', label: 'Proposal sent' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
];
const deliveryStageOptions: Array<{ value: ClientDeliveryStage; label: string }> = [
    { value: 'pre_engagement', label: 'Pre-engagement' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'complete', label: 'Complete' },
];
const draft = reactive({
    name: '',
    legalName: '',
    entityType: 'organization' as 'individual' | 'organization',
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    lifecycleStatus: 'raw_enquiry' as ClientLifecycleStatus,
    lifecycleSource: 'manual' as ClientSourceType,
    commercialStage: 'lead' as ClientCommercialStage,
    pipelineStage: 'new_enquiry' as ClientPipelineStage,
    deliveryStage: 'pre_engagement' as ClientDeliveryStage,
    health: '',
    notes: '',
    tags: '',
    annualValueTarget: '',
    annualValueCurrency: 'GBP',
    avatarURL: '',
    headerImageURL: '',
});

function currentValues() {
    return {
        name: props.client?.name || '',
        legalName: props.client?.legalName || '',
        entityType: props.client?.entityType || 'organization',
        companyName: props.client?.companyName || '',
        contactName: props.client?.contactName || '',
        email: props.client?.email || '',
        phone: props.client?.contactNumber || props.client?.phone || '',
        website: props.client?.website || '',
        address: props.client?.address || '',
        lifecycleStatus: props.client?.lifecycle?.status || 'raw_enquiry',
        lifecycleSource: (props.client?.lifecycle?.source === 'client_record' ? 'manual' : props.client?.lifecycle?.source) || 'manual',
        commercialStage: props.client?.lifecycle?.commercialStage || 'lead',
        pipelineStage: props.client?.lifecycle?.pipelineStage || 'new_enquiry',
        deliveryStage: props.client?.lifecycle?.deliveryStage || 'pre_engagement',
        health: props.client?.lifecycle?.health || '',
        notes: props.client?.lifecycle?.notes || '',
        tags: (props.client?.lifecycle?.tags || []).join(', '),
        annualValueTarget: props.client?.lifecycle?.annualValueTarget?.toString() || '',
        annualValueCurrency: props.client?.lifecycle?.annualValueCurrency || 'GBP',
        avatarURL: props.client?.avatarURL || '',
        headerImageURL: props.client?.headerImageURL || '',
    };
}

function normalizedDraft() {
    return {
        ...draft,
        name: draft.name.trim(),
        legalName: draft.legalName.trim(),
        companyName: draft.companyName.trim(),
        contactName: draft.contactName.trim(),
        email: draft.email.trim().toLowerCase(),
        phone: draft.phone.trim(),
        website: draft.website.trim(),
        address: draft.address.trim(),
        health: draft.health.trim(),
        notes: draft.notes.trim(),
        tags: draft.tags.split(',').map(tag => tag.trim()).filter(Boolean).join(', '),
        annualValueTarget: draft.annualValueTarget.trim(),
        annualValueCurrency: draft.annualValueCurrency.trim().toUpperCase(),
        avatarURL: draft.avatarURL.trim(),
        headerImageURL: draft.headerImageURL.trim(),
    };
}

function reset() {
    Object.assign(draft, currentValues());
}

const dirty = computed(() => Boolean(props.client) && JSON.stringify(normalizedDraft()) !== JSON.stringify(currentValues()));
const valid = computed(() => Boolean(draft.name.trim()));

async function saveDetails() {
    if (!props.client || !dirty.value || !valid.value) return;
    saving.value = true;
    try {
        const values = normalizedDraft();
        const annualValueTarget = values.annualValueTarget === '' ? undefined : Number(values.annualValueTarget);
        await props.save({
            name: values.name,
            legalName: values.legalName,
            entityType: values.entityType,
            companyName: values.companyName,
            contactName: values.contactName,
            email: values.email,
            phone: values.phone,
            contactNumber: values.phone,
            website: values.website,
            address: values.address,
            avatarURL: values.avatarURL,
            headerImageURL: values.headerImageURL,
            lifecycle: {
                ...(props.client.lifecycle || {}),
                status: values.lifecycleStatus,
                source: values.lifecycleSource,
                commercialStage: values.commercialStage,
                pipelineStage: values.pipelineStage,
                deliveryStage: values.deliveryStage,
                health: values.health || undefined,
                notes: values.notes || undefined,
                tags: values.tags ? values.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
                annualValueTarget: Number.isFinite(annualValueTarget) ? annualValueTarget : undefined,
                annualValueCurrency: values.annualValueCurrency || undefined,
            },
        });
        emit('update:open', false);
    } catch (error: any) {
        toast.error(error?.message || 'Could not save client settings.');
    } finally {
        saving.value = false;
    }
}

watch(() => [props.open, props.client] as const, ([open]) => {
    if (open) {
        reset();
    } else {
        deleteConfirmationOpen.value = false;
    }
}, { immediate: true });
</script>

<template>
    <DetailsSheet
        :open="open"
        size="wide"
        title="Client settings"
        description="Edit the complete client record while keeping the workspace in view. Contacts and lifecycle belong here; invitations and destructive actions remain focused decisions."
        :dirty="dirty"
        :saving="saving"
        :save-disabled="!valid"
        @update:open="emit('update:open', $event)"
        @cancel="reset"
        @save="saveDetails"
    >
        <div class="space-y-8">
            <div class="space-y-8">
                <section class="space-y-4">
                    <div><h3 class="text-sm font-semibold">Identity</h3><p class="text-sm text-muted-foreground">The names and account type used throughout Vuey.</p></div>
                    <div class="space-y-2"><Label for="client-settings-name">Display name</Label><Input id="client-settings-name" v-model="draft.name" /></div>
                    <div class="space-y-4">
                        <div class="space-y-2"><Label for="client-settings-company">Company name</Label><Input id="client-settings-company" v-model="draft.companyName" /></div>
                        <div class="space-y-2"><Label for="client-settings-legal">Legal name</Label><Input id="client-settings-legal" v-model="draft.legalName" /></div>
                    </div>
                    <div class="space-y-2">
                        <Label for="client-settings-type">Account type</Label>
                        <SearchableSelectMenu id="client-settings-type" v-model="draft.entityType" :options="entityTypeOptions" show-all-by-default variant="form" />
                    </div>
                    <div class="space-y-4">
                        <div class="space-y-2"><Label for="client-settings-contact">Primary contact name</Label><Input id="client-settings-contact" v-model="draft.contactName" /></div>
                        <div class="space-y-2"><Label for="client-settings-email">Primary email</Label><Input id="client-settings-email" v-model="draft.email" type="email" /></div>
                    </div>
                    <div class="space-y-4">
                        <div class="space-y-2"><Label for="client-settings-phone">Phone</Label><Input id="client-settings-phone" v-model="draft.phone" /></div>
                        <div class="space-y-2"><Label for="client-settings-website">Website</Label><Input id="client-settings-website" v-model="draft.website" type="url" /></div>
                    </div>
                    <div class="space-y-2"><Label for="client-settings-address">Address</Label><Textarea id="client-settings-address" v-model="draft.address" rows="3" /></div>
                </section>

                <section class="space-y-4 border-t pt-6">
                    <div><h3 class="text-sm font-semibold">Lifecycle</h3><p class="text-sm text-muted-foreground">Commercial stage, delivery state, and how this client first reached you.</p></div>
                    <div class="space-y-4">
                        <div class="space-y-2"><Label for="client-settings-lifecycle">Lifecycle</Label><SearchableSelectMenu id="client-settings-lifecycle" v-model="draft.lifecycleStatus" :options="lifecycleOptions" show-all-by-default variant="form" /></div>
                        <div class="space-y-2"><Label for="client-settings-source">Source</Label><SearchableSelectMenu id="client-settings-source" v-model="draft.lifecycleSource" :options="sourceOptions" show-all-by-default variant="form" /></div>
                    </div>
                    <div class="space-y-4">
                        <div class="space-y-2"><Label for="client-settings-commercial-stage">Commercial stage</Label><SearchableSelectMenu id="client-settings-commercial-stage" v-model="draft.commercialStage" :options="commercialStageOptions" show-all-by-default variant="form" /></div>
                        <div class="space-y-2"><Label for="client-settings-pipeline-stage">Pipeline stage</Label><SearchableSelectMenu id="client-settings-pipeline-stage" v-model="draft.pipelineStage" :options="pipelineStageOptions" show-all-by-default variant="form" /></div>
                        <div class="space-y-2"><Label for="client-settings-delivery-stage">Delivery stage</Label><SearchableSelectMenu id="client-settings-delivery-stage" v-model="draft.deliveryStage" :options="deliveryStageOptions" show-all-by-default variant="form" /></div>
                    </div>
                    <div class="space-y-4">
                        <div class="space-y-2"><Label for="client-settings-health">Health</Label><Input id="client-settings-health" v-model="draft.health" /></div>
                        <div class="space-y-2"><Label for="client-settings-tags">Tags</Label><Input id="client-settings-tags" v-model="draft.tags" placeholder="retainer, priority" /></div>
                    </div>
                    <div class="space-y-2"><Label for="client-settings-notes">Lifecycle and handoff notes</Label><Textarea id="client-settings-notes" v-model="draft.notes" rows="5" /></div>
                    <div class="space-y-4">
                        <div class="space-y-2"><Label for="client-settings-value">Annual value target</Label><Input id="client-settings-value" v-model="draft.annualValueTarget" type="number" min="0" /></div>
                        <div class="space-y-2"><Label for="client-settings-currency">Currency</Label><Input id="client-settings-currency" v-model="draft.annualValueCurrency" maxlength="3" /></div>
                    </div>
                </section>
            </div>

            <div class="space-y-8">
                <section class="space-y-4">
                    <div><h3 class="text-sm font-semibold">Media</h3><p class="text-sm text-muted-foreground">Existing media URLs. Direct upload actions can continue to live on the workspace hero.</p></div>
                    <div class="space-y-2"><Label for="client-settings-avatar">Avatar URL</Label><Input id="client-settings-avatar" v-model="draft.avatarURL" type="url" /></div>
                    <div class="space-y-2"><Label for="client-settings-header">Header image URL</Label><Input id="client-settings-header" v-model="draft.headerImageURL" type="url" /></div>
                </section>

                <section class="space-y-4 border-t pt-6">
                    <div><h3 class="text-sm font-semibold">Contacts</h3><p class="text-sm text-muted-foreground">People attached to this Client record.</p></div>
                    <div class="divide-y rounded-lg border">
                        <div v-for="contact in client?.contacts || []" :key="contact.id" class="flex items-center gap-3 px-4 py-3">
                            <div class="flex size-9 items-center justify-center rounded-full bg-muted"><UserRound class="size-4" /></div>
                            <div class="min-w-0 flex-1"><p class="truncate text-sm font-medium">{{ contact.name }}</p><p class="flex items-center gap-1 truncate text-xs text-muted-foreground"><Mail class="size-3" />{{ contact.email || 'No email' }}</p></div>
                            <span v-if="contact.isPrimary" class="text-xs text-muted-foreground">Primary</span>
                            <Button type="button" variant="outline" size="sm" @click="emit('edit-contact', contact)">Edit</Button>
                        </div>
                        <div v-if="!(client?.contacts || []).length" class="px-4 py-6 text-center text-sm text-muted-foreground">No contacts have been added yet.</div>
                    </div>
                    <Button type="button" variant="outline" class="w-full" @click="emit('add-contact')"><Plus class="mr-2 size-4" />Add client contact</Button>
                </section>
            </div>

            <section v-if="canDelete" class="space-y-4 border-t border-destructive/25 pt-6">
                <div>
                    <h3 class="text-sm font-semibold text-destructive">Delete client</h3>
                    <p class="text-sm leading-6 text-muted-foreground">
                        Remove this Client workspace after confirming the linked records that will remain for history.
                    </p>
                </div>
                <Button type="button" variant="destructive" :disabled="deleting" @click="deleteConfirmationOpen = true">
                    <Trash2 class="mr-2 size-4" />Delete client
                </Button>
            </section>
        </div>
        <p v-if="!valid" class="mt-6 text-sm text-destructive">A client display name is required.</p>
    </DetailsSheet>

    <ConfirmDialog
        v-model:open="deleteConfirmationOpen"
        :title="`Delete ${client?.name || 'this client'}?`"
        description="This permanently removes the Client workspace and revokes its portal access. Linked projects, quotes, invoices, contracts, files, and activity records are retained with their existing clientId for audit and recovery. This cannot be undone from the app."
        confirm-label="Delete client"
        destructive
        :loading="deleting"
        @confirm="emit('delete')"
    />
</template>
